import { useState, useRef, useEffect } from 'react';
import { authGet, authPost } from '../../services/apiService';

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const mentionToken = (person) => `@${person.displayName}`;

/**
 * ADO discussion-comment composer for a work item: open/close the dialog,
 * load existing comments, post a new one, and the @mention identity-search
 * autocomplete. Extracted out of TicketBacklogTable, where this was ~155
 * lines of state and handlers inline in a 1,100-line component -- one of
 * five separable concerns that component bundled together.
 */
export const useAdoCommentComposer = () => {
  const [commentTicket, setCommentTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentMentions, setCommentMentions] = useState([]);
  const [commentPosting, setCommentPosting] = useState(false);
  const [commentPostError, setCommentPostError] = useState(null);
  const [identityOptions, setIdentityOptions] = useState([]);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [identityError, setIdentityError] = useState(null);
  const [identityInput, setIdentityInput] = useState('');

  const mountedRef = useRef(true);
  const commentRequestRef = useRef(0);
  const identityTimerRef = useRef(null);
  const identityRequestRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (identityTimerRef.current) clearTimeout(identityTimerRef.current);
    };
  }, []);

  const resetCommentCompose = () => {
    identityRequestRef.current += 1;
    if (identityTimerRef.current) clearTimeout(identityTimerRef.current);
    setCommentDraft('');
    setCommentMentions([]);
    setCommentPostError(null);
    setCommentPosting(false);
    setIdentityOptions([]);
    setIdentityLoading(false);
    setIdentityError(null);
    setIdentityInput('');
  };

  const loadCommentsForIds = (ids, requestId, { showSpinner = true } = {}) => {
    if (showSpinner) setCommentsLoading(true);
    Promise.allSettled(ids.map((id) => authGet(`/api/customer-issues/ado-comments/${id}`)))
      .then((results) => {
        if (!mountedRef.current || commentRequestRef.current !== requestId) return;
        const merged = [];
        let anyFailed = false;
        ids.forEach((id, index) => {
          const result = results[index];
          if (result.status === 'fulfilled') {
            (result.value.comments || []).forEach((comment) => {
              merged.push({ ...comment, workItemId: id });
            });
          } else {
            anyFailed = true;
          }
        });
        setComments(merged);
        if (anyFailed && merged.length === 0) {
          setCommentsError('Failed to load comments');
        } else {
          setCommentsError(null);
        }
      })
      .finally(() => {
        if (mountedRef.current && commentRequestRef.current === requestId) setCommentsLoading(false);
      });
  };

  /** Same open sequence 3 call sites in TicketBacklogTable used to repeat inline -- now one call. */
  const openComments = (ticket, adoId, commentIds) => {
    const requestId = ++commentRequestRef.current;
    setCommentTicket({ ...ticket, adoId, commentIds });
    resetCommentCompose();
    setComments([]);
    setCommentsError(null);
    loadCommentsForIds(commentIds, requestId);
  };

  const closeComments = () => {
    commentRequestRef.current += 1;
    setCommentTicket(null);
    resetCommentCompose();
  };

  const searchIdentities = (query) => {
    if (identityTimerRef.current) clearTimeout(identityTimerRef.current);
    const q = String(query || '').trim().replace(/^@+/, '');
    if (q.length < 2) {
      setIdentityOptions([]);
      setIdentityError(null);
      setIdentityLoading(false);
      return;
    }
    setIdentityLoading(true);
    setIdentityError(null);
    const requestId = ++identityRequestRef.current;
    identityTimerRef.current = setTimeout(async () => {
      try {
        const res = await authGet(`/api/customer-issues/ado-identities?q=${encodeURIComponent(q)}`);
        if (!mountedRef.current || identityRequestRef.current !== requestId) return;
        setIdentityOptions(res.identities || []);
        setIdentityError(null);
      } catch (err) {
        if (!mountedRef.current || identityRequestRef.current !== requestId) return;
        setIdentityOptions([]);
        setIdentityError(err.message || 'Failed to search ADO people');
      } finally {
        if (mountedRef.current && identityRequestRef.current === requestId) setIdentityLoading(false);
      }
    }, 300);
  };

  const handleMentionsChange = (_event, next) => {
    const selected = next || [];
    const nextIds = new Set(selected.map((person) => person.id));
    const removed = commentMentions.filter((person) => !nextIds.has(person.id));
    setCommentMentions(selected);
    setCommentDraft((prev) => {
      let nextDraft = prev;
      removed.forEach((person) => {
        nextDraft = nextDraft.replace(new RegExp(`${escapeRegExp(mentionToken(person))}\\s?`, 'g'), '');
      });
      selected.forEach((person) => {
        const token = mentionToken(person);
        if (!nextDraft.includes(token)) {
          nextDraft = `${nextDraft}${nextDraft && !/\s$/.test(nextDraft) ? ' ' : ''}${token} `;
        }
      });
      return nextDraft.replace(/[ \t]{2,}/g, ' ');
    });
  };

  const handlePostComment = async () => {
    const workItemId = commentTicket && commentTicket.adoId;
    const text = commentDraft.trim();
    if (!workItemId || commentPosting) return;
    if (!text && commentMentions.length === 0) return;
    setCommentPosting(true);
    setCommentPostError(null);
    try {
      const result = await authPost(`/api/customer-issues/ado-comments/${workItemId}`, {
        text,
        mentions: commentMentions.map(({ id, displayName }) => ({ id, displayName }))
      }, { timeout: 20000 });
      if (!mountedRef.current) return;
      if (result && result.comment && result.comment.text) {
        setComments((prev) => [result.comment, ...prev]);
      }
      setCommentDraft('');
      setCommentMentions([]);
      setIdentityOptions([]);
      setIdentityInput('');
      const ids = (commentTicket.commentIds && commentTicket.commentIds.length)
        ? commentTicket.commentIds
        : [workItemId];
      const requestId = ++commentRequestRef.current;
      loadCommentsForIds(ids, requestId, { showSpinner: false });
    } catch (err) {
      if (mountedRef.current) setCommentPostError(err.message || 'Failed to post comment to ADO');
    } finally {
      if (mountedRef.current) setCommentPosting(false);
    }
  };

  const canPostComment = Boolean(commentTicket && commentTicket.adoId)
    && !commentPosting
    && (commentDraft.trim().length > 0 || commentMentions.length > 0);

  return {
    commentTicket,
    comments,
    commentsLoading,
    commentsError,
    commentDraft,
    setCommentDraft,
    commentMentions,
    commentPosting,
    commentPostError,
    identityOptions,
    identityLoading,
    identityError,
    identityInput,
    setIdentityInput,
    openComments,
    closeComments,
    searchIdentities,
    handleMentionsChange,
    handlePostComment,
    canPostComment
  };
};
